// PendingCasesPage.tsx
/**
 * Leader Pending Cases — cases assigned to the current leader awaiting
 * accept/decline. Accepting or declining a case refreshes this list in
 * place. When there are no pending cases, an empty state offers a button
 * to jump over to the leader's full case list (/Counseling/MyCases).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Badge,
  Button,
  Group,
  Stack,
  SimpleGrid,
  ActionIcon,
  Tooltip,
  Skeleton,
  Avatar,
} from '@mantine/core';
import {
  IconRefresh,
  IconCheck,
  IconX,
  IconCalendarEvent,
  IconUsers,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useLeaderPortalService, LeaderCaseView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { EmptyState } from '@components/ccms-components/utils/EmptyState';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import PendingCaseModal from './Components/PendingCaseModal';
import LeaderPortalNotice from '../Components/LeaderPortalNotice';

function formatSDKDate(value: number | string | null | undefined): string {
  const raw = String(value ?? '');
  if (raw.length < 8) return '—';
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
}

export default function PendingCasesPage() {
  const navigate = useNavigate();
  const leaderPortalService = useLeaderPortalService();
  const { refetch } = useSidebarCountsContext();

  const [cases, setCases] = useState<LeaderCaseView[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  // Tracks per-case action in flight so only that card's buttons disable/spin.
  const [actioningCaseID, setActioningCaseID] = useState<number | null>(null);
  // Card click opens the compact review modal (summary + note history + accept/reject).
  // `rejectTarget` opens the same modal straight into the reason form.
  const [selectedCase, setSelectedCase] = useState<LeaderCaseView | null>(null);
  const [rejectTarget, setRejectTarget] = useState<LeaderCaseView | null>(null);
  const modalCase = rejectTarget ?? selectedCase;

  const closeModal = () => { setSelectedCase(null); setRejectTarget(null); };

  const fetchPendingCases = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await leaderPortalService.myPendingCases();
      setCases(Array.isArray(result) ? result : []);
      refetch(); // Update sidebar counts
    } catch (e) {
      // Surfaced inline rather than as a toast. A 403 here means the account has no leader record,
      // which is a setup problem the user needs to read and act on, not a transient blip.
      console.error('Error fetching pending cases:', e);
      setLoadError(e);
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [leaderPortalService, refetch]);

  useEffect(() => { fetchPendingCases(); }, [fetchPendingCases]);

  const handleAccept = async (caseID: number) => {
    setActioningCaseID(caseID);
    try {
      await leaderPortalService.acceptCase(caseID);
      toast.success('Case accepted', 'The case has been moved to your active cases.');
      await fetchPendingCases(); // refresh the list
      refetch(); // Update sidebar counts
    } catch (e) {
      console.error('Error accepting case:', e);
      toast.error('Error', 'Could not accept the case.');
    } finally {
      setActioningCaseID(null);
    }
  };

  // Declining lives in PendingCaseModal — it has to capture a reason and write it to the case
  // as a restricted note before calling declineCase().

  const handleRefresh = () => fetchPendingCases();

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" align="flex-start" mb="lg">
        <div>
          <Title order={2}>Pending Cases</Title>
          <Text c="dimmed" size="sm">
            Cases assigned to you that are waiting to be accepted or declined.
          </Text>
        </div>
        <Tooltip label="Refresh">
          <ActionIcon variant="light" size="lg" onClick={handleRefresh} loading={loading}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {loadError ? (
        <LeaderPortalNotice
          error={loadError}
          subject="your pending cases"
          onRetry={fetchPendingCases}
        />
      ) : loading && cases === null ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} withBorder radius="md" padding="lg">
              <Skeleton height={16} width="70%" mb={8} />
              <Skeleton height={12} width="40%" mb="md" />
              <Skeleton height={12} width="90%" mb={6} />
              <Skeleton height={12} width="60%" mb="md" />
              <Skeleton height={32} width="100%" />
            </Card>
          ))}
        </SimpleGrid>
      ) : !cases || cases.length === 0 ? (
        <EmptyState
          variant="no-data"
          entityLabel="pending cases"
          onAdd={() => navigate('/Counselling/MyCases')}
          addLabel="View My Cases"
        />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {cases.map((c) => {
            const isActioning = actioningCaseID === c.caseID;
            return (
              <Card
                key={c.caseID}
                withBorder
                radius="md"
                padding="lg"
                onClick={() => setSelectedCase(c)}
                style={{ cursor: 'pointer' }}
              >
                <Stack gap="xs" style={{ height: '100%' }}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Text fw={600} lineClamp={1}>
                      {c.name ?? `Case ${c.caseID}`}
                    </Text>
                    <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(c.statusColor)}>
                      {c.statusName ?? '—'}
                    </Badge>
                  </Group>

                  {c.needName && (
                    <Badge
                      variant="filled"
                      styles={badgeStyles}
                      style={{ ...brandBadgeStyle(c.needColor), alignSelf: 'flex-start' }}
                    >
                      {c.needName}
                    </Badge>
                  )}

                  <Group gap={6} wrap="nowrap">
                    <IconUsers size={14} opacity={0.6} />
                    <Text size="sm" c="dimmed" lineClamp={1}>
                      {c.counseleeNames ?? '—'}
                    </Text>
                  </Group>

                  <Group gap={6} wrap="nowrap">
                    <IconCalendarEvent size={14} opacity={0.6} />
                    <Text size="sm" c="dimmed">
                      {c.nextSessionDisplay ?? (c.nextSessionDateTime ? formatSDKDate(c.nextSessionDateTime) : 'No session scheduled')}
                    </Text>
                  </Group>

                  <Text size="xs" c="dimmed" mt={2}>
                    Opened {formatSDKDate(c.caseDateTime)}
                    {typeof c.daysSinceLastActivity === 'number' && (
                      <> · {c.daysSinceLastActivity}d since last activity</>
                    )}
                  </Text>

                  {/* Decline routes through the modal so a reason is always captured; Accept
                      needs no reason, so it stays a one-click action on the card. */}
                  <Group grow mt="auto" pt="sm">
                    <Button
                      variant="light"
                      color="red"
                      leftSection={<IconX size={16} />}
                      onClick={(e) => { e.stopPropagation(); setRejectTarget(c); }}
                      disabled={isActioning}
                    >
                      Decline
                    </Button>
                    <Button
                      color="green"
                      leftSection={<IconCheck size={16} />}
                      onClick={(e) => { e.stopPropagation(); handleAccept(c.caseID); }}
                      loading={isActioning}
                      disabled={isActioning}
                    >
                      Accept
                    </Button>
                  </Group>
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>
      )}

      <PendingCaseModal
        careCase={modalCase}
        opened={modalCase !== null}
        startInReject={rejectTarget !== null}
        onClose={closeModal}
        onActioned={fetchPendingCases}
      />
    </Container>
  );
}